import { useState } from 'react'
import type { DebugMode } from '../engine/debugGameSetup'
import { DEBUG_MODES } from '../engine/debugGameSetup'
import { useTheme } from '../hooks/useTheme'
import { GAME_COLORS } from '../design/premiumTokens'

export interface PlayerCountSelectionProps {
  onSelect: (count: number, playerStarts?: boolean, debugMode?: DebugMode) => void
  onBack: () => void
}

// Artist color accent for each player count
const PLAYER_COUNT_ARTIST_COLORS: Record<number, string> = {
  3: GAME_COLORS.artists['Solstice Vane'].primary,   // Gold
  4: GAME_COLORS.artists['Mira Faux'].primary,       // Teal
  5: GAME_COLORS.artists['Luxe Nebula'].primary,     // Purple
}

export function PlayerCountSelection({
  onSelect,
  onBack,
}: PlayerCountSelectionProps) {
  const [playerStarts, setPlayerStarts] = useState(false)
  const [debugMode, setDebugMode] = useState<DebugMode>('none')
  const [selectedCount, setSelectedCount] = useState<number | null>(null)
  const theme = useTheme()

  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  const handleStartGame = () => {
    if (selectedCount !== null) {
      onSelect(selectedCount, playerStarts, debugMode === 'none' ? undefined : debugMode)
    }
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      {/* Fixed Header with Back Button */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
        }}
      >
        <div
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'none',
              border: 'none',
              color: theme.textSecondary,
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = theme.primary
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = theme.textSecondary
            }}
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" strokeWidth={2}>
              <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back
          </button>

          <h1
            style={{
              fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
              fontWeight: 200,
              letterSpacing: '0.15em',
              margin: 0,
              color: theme.primary,
              textShadow: theme.shadow,
            }}
          >
            SELECT PLAYERS
          </h1>

          <div style={{ width: '80px' }} />
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 10,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 24px',
        }}
      >
        {/* Who should start option - Card-style buttons */}
        <div style={{ marginBottom: 48 }}>
          <div style={{ display: 'flex', gap: 20, justifyContent: 'center' }}>
            {/* You go first option */}
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '16px 24px',
                borderRadius: 12,
                backgroundColor: playerStarts ? theme.primaryMuted : 'rgba(255, 255, 255, 0.15)',
                border: playerStarts ? `2px solid ${theme.primary}` : `2px solid ${theme.border}`,
                transition: 'all 0.2s ease',
                minWidth: '160px',
              }}
              onMouseEnter={(e) => {
                if (!playerStarts) {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.22)'
                  e.currentTarget.style.borderColor = theme.borderHover
                }
              }}
              onMouseLeave={(e) => {
                if (!playerStarts) {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'
                  e.currentTarget.style.borderColor = theme.border
                }
              }}
            >
              <input
                type="radio"
                name="whoStarts"
                checked={playerStarts}
                onChange={() => setPlayerStarts(true)}
                style={{ margin: 0 }}
              />
              <span style={{
                color: playerStarts ? theme.primary : theme.text,
                fontSize: '0.95rem',
                fontWeight: 500,
              }}>
                You go first
              </span>
              <span style={{
                color: theme.textMuted,
                fontSize: '0.75rem',
                marginTop: 2,
              }}>
                Start as bidder
              </span>
            </label>

            {/* Random draw option */}
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '16px 24px',
                borderRadius: 12,
                backgroundColor: !playerStarts ? theme.primaryMuted : 'rgba(255, 255, 255, 0.15)',
                border: !playerStarts ? `2px solid ${theme.primary}` : `2px solid ${theme.border}`,
                transition: 'all 0.2s ease',
                minWidth: '160px',
              }}
              onMouseEnter={(e) => {
                if (playerStarts) {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.22)'
                  e.currentTarget.style.borderColor = theme.borderHover
                }
              }}
              onMouseLeave={(e) => {
                if (playerStarts) {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'
                  e.currentTarget.style.borderColor = theme.border
                }
              }}
            >
              <input
                type="radio"
                name="whoStarts"
                checked={!playerStarts}
                onChange={() => setPlayerStarts(false)}
                style={{ margin: 0 }}
              />
              <span style={{
                color: !playerStarts ? theme.primary : theme.text,
                fontSize: '0.95rem',
                fontWeight: 500,
              }}>
                Random draw
              </span>
              <span style={{
                color: theme.textMuted,
                fontSize: '0.75rem',
                marginTop: 2,
              }}>
                Let chance decide
              </span>
            </label>
          </div>
        </div>

        {/* Debug Mode option - only show on localhost */}
        {isLocalhost && (
          <div style={{ marginBottom: 40 }}>
            <p style={{
              color: theme.textSecondary,
              fontSize: '0.85rem',
              marginBottom: 12,
              textAlign: 'center'
            }}>
              Debug Mode
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {DEBUG_MODES.map((mode) => (
                <label
                  key={mode.value}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    cursor: 'pointer',
                    padding: '10px 16px',
                    borderRadius: 8,
                    backgroundColor: debugMode === mode.value ? theme.primaryMuted : theme.card,
                    border: debugMode === mode.value ? `2px solid ${theme.primaryMuted.replace('0.2', '0.5')}` : `2px solid ${theme.border}`,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = debugMode === mode.value
                      ? theme.primaryMuted.replace('0.2', '0.25')
                      : theme.cardHover
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = debugMode === mode.value
                      ? theme.primaryMuted
                      : theme.card
                  }}
                >
                  <input
                    type="radio"
                    name="debugMode"
                    value={mode.value}
                    checked={debugMode === mode.value}
                    onChange={() => setDebugMode(mode.value)}
                    style={{
                      margin: 0,
                      width: 18,
                      height: 18,
                      cursor: 'pointer',
                      marginTop: 2
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{
                      color: debugMode === mode.value ? theme.primary : theme.text,
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}>
                      {mode.label}
                    </span>
                    <span style={{
                      color: theme.textMuted,
                      fontSize: '0.75rem'
                    }}>
                      {mode.description}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Player count cards - Gallery-style with artist color accents */}
        <div style={{ marginBottom: 40 }}>
          <p style={{
            color: theme.textSecondary,
            fontSize: '0.9rem',
            marginBottom: 20,
            textAlign: 'center',
            fontWeight: 400,
          }}>
            Select number of players
          </p>
          <div style={{ display: 'flex', gap: 32 }}>
            {[3, 4, 5].map((count) => {
              const isSelected = selectedCount === count
              const artistColor = PLAYER_COUNT_ARTIST_COLORS[count]

              return (
                <button
                  key={count}
                  onClick={() => setSelectedCount(count)}
                  style={{
                    position: 'relative',
                    width: 160,
                    height: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    backgroundColor: theme.card,
                    border: isSelected ? `3px solid ${artistColor}` : `2px solid ${theme.border}`,
                    borderTop: `4px solid ${artistColor}`,
                    borderRadius: 12,
                    color: theme.text,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: isSelected ? `0 0 20px ${artistColor}40` : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.transform = 'scale(1.05)'
                      e.currentTarget.style.borderColor = theme.primary
                      e.currentTarget.style.boxShadow = theme.shadow
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.transform = 'scale(1)'
                      e.currentTarget.style.borderColor = theme.border
                      e.currentTarget.style.boxShadow = 'none'
                    }
                  }}
                >
                  <span style={{
                    fontSize: '4rem',
                    fontWeight: 200,
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                    color: isSelected ? artistColor : theme.text,
                  }}>
                    {count}
                  </span>
                  <span style={{
                    fontSize: '0.85rem',
                    letterSpacing: '0.1em',
                    color: isSelected ? artistColor : theme.textSecondary,
                    textTransform: 'uppercase',
                  }}>
                    players
                  </span>
                  {isSelected && (
                    <div style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: artistColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <svg width={12} height={12} viewBox="0 0 24 24" fill="none" strokeWidth={3}>
                        <path d="M20 6L9 17l-5-5" stroke="#000" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Start Game Button */}
        <button
          onClick={handleStartGame}
          disabled={selectedCount === null}
          style={{
            padding: '18px 56px',
            fontSize: '1rem',
            fontWeight: 500,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: selectedCount !== null ? '#0a0a0a' : theme.textMuted,
            background: selectedCount !== null ? theme.buttonPrimary : `${theme.buttonPrimary}40`,
            border: 'none',
            borderRadius: 8,
            cursor: selectedCount !== null ? 'pointer' : 'not-allowed',
            boxShadow: selectedCount !== null ? theme.shadow : 'none',
            transition: 'all 0.2s ease',
            opacity: selectedCount !== null ? 1 : 0.5,
          }}
          onMouseEnter={(e) => {
            if (selectedCount !== null) {
              e.currentTarget.style.transform = 'scale(1.05)'
              e.currentTarget.style.boxShadow = theme.primaryHover === '#E5C158'
                ? '0 8px 40px rgba(201,162,39,0.4)'
                : theme.shadow
            }
          }}
          onMouseLeave={(e) => {
            if (selectedCount !== null) {
              e.currentTarget.style.transform = 'scale(1)'
              e.currentTarget.style.boxShadow = theme.shadow
            }
          }}
          onMouseDown={(e) => {
            if (selectedCount !== null) {
              e.currentTarget.style.transform = 'scale(0.98)'
            }
          }}
          onMouseUp={(e) => {
            if (selectedCount !== null) {
              e.currentTarget.style.transform = 'scale(1.05)'
            }
          }}
        >
          Start Game
        </button>
      </div>
    </div>
  )
}
